// Live-demo Send form. Fires a real kind=0 Transfer through the staging
// cluster's /api/tx endpoint. The mesh node signs+admits as itself, so
// the resulting tx is genuinely on the DAG and shows up in the live
// feed within ~5 seconds.

import { useState } from "react";
import { Link } from "react-router-dom";

const NODES = [
  {
    idx: 0,
    aid: "51f1365add907c3dcaead8fae90cfe5590263824",
    label: "node-0 (faucet)",
  },
  {
    idx: 1,
    aid: "3d704cf6a3625fe5b6a66ccfc44db2fc707a69e1",
    label: "node-1",
  },
  {
    idx: 2,
    aid: "b0f76482ce3939c966f9a5ddf639298ffa7db840",
    label: "node-2",
  },
  {
    idx: 3,
    aid: "b2db92e2369e565e1a26c58ea91a9a45e77b1f25",
    label: "node-3",
  },
  {
    idx: 4,
    aid: "5e36f2b0b8a87aadb66bfda96d42700783d770a7",
    label: "node-4",
  },
];

interface SendResult {
  ok: boolean;
  tx_id?: string;
  from?: string;
  to?: string;
  amount_micros?: number;
  logical_time?: number;
  error?: string;
}

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export default function SendPage() {
  const [fromNode, setFromNode] = useState(0);
  const [to, setTo] = useState(NODES[1]!.aid);
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await fetch(`${API_BASE}/api/tx`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          node: fromNode,
          to,
          amount_micros: amount,
        }),
      });
      const data = (await r.json()) as SendResult;
      if (!data.ok) {
        setError(data.error ?? "unknown error");
      } else {
        setResult(data);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Send a real transaction</h1>
        <p className="text-sm text-mesh-muted mt-1">
          Fires a kind=0 Transfer through the live 5-node staging cluster on
          AWS. The chosen node signs the tx with its real ed25519 keypair,
          solves PoW, and admits it via the gossipsub <code>RealValidator</code>
          pipeline. Tx finalizes after the next 2-of-N witness round
          (~3 seconds).
        </p>
      </header>

      <form onSubmit={send} className="space-y-4 bg-mesh-card p-5 rounded-lg">
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-mesh-muted">
            From (sender)
          </span>
          <select
            value={fromNode}
            onChange={(e) => setFromNode(Number(e.target.value))}
            className="mt-1 w-full bg-mesh-bg border border-mesh-border rounded px-3 py-2"
          >
            {NODES.map((n) => (
              <option key={n.idx} value={n.idx}>
                {n.label} — {n.aid.slice(0, 12)}…
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-wide text-mesh-muted">
            To (40-hex account_id)
          </span>
          <select
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 w-full bg-mesh-bg border border-mesh-border rounded px-3 py-2"
          >
            {NODES.map((n) => (
              <option key={n.idx} value={n.aid}>
                {n.label} — {n.aid.slice(0, 12)}…
              </option>
            ))}
          </select>
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="or paste a 40-hex account_id"
            maxLength={40}
            minLength={40}
            className="mt-2 w-full bg-mesh-bg border border-mesh-border rounded px-3 py-2 font-mono text-sm"
          />
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-wide text-mesh-muted">
            Amount (µ)
          </span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            min={1}
            className="mt-1 w-full bg-mesh-bg border border-mesh-border rounded px-3 py-2"
          />
        </label>

        <button
          type="submit"
          disabled={busy || to.length !== 40}
          className="bg-mesh-accent text-mesh-bg px-4 py-2 rounded font-medium disabled:opacity-40"
        >
          {busy ? "Submitting…" : "Send transaction"}
        </button>
      </form>

      {error && (
        <div className="bg-red-950/40 border border-red-800 text-red-300 p-4 rounded">
          <div className="font-medium">Error</div>
          <div className="text-sm font-mono mt-1">{error}</div>
        </div>
      )}

      {result?.ok && (
        <div className="bg-emerald-950/40 border border-emerald-800 text-emerald-200 p-4 rounded space-y-2">
          <div className="font-medium">Submitted ✓</div>
          <div className="text-sm space-y-1 font-mono">
            <div>
              <span className="text-mesh-muted">tx_id: </span>
              {result.tx_id && (
                <Link
                  to={`/tx/${result.tx_id}`}
                  className="underline break-all"
                >
                  {result.tx_id}
                </Link>
              )}
            </div>
            <div>
              <span className="text-mesh-muted">from: </span>
              <Link
                to={`/account/${result.from}`}
                className="underline break-all"
              >
                {result.from}
              </Link>
            </div>
            <div>
              <span className="text-mesh-muted">to: </span>
              <Link
                to={`/account/${result.to}`}
                className="underline break-all"
              >
                {result.to}
              </Link>
            </div>
            <div>
              <span className="text-mesh-muted">amount: </span>
              {result.amount_micros}µ
            </div>
            <div>
              <span className="text-mesh-muted">logical_time: </span>
              {result.logical_time}
            </div>
          </div>
          <p className="text-xs text-mesh-muted pt-2 border-t border-emerald-800">
            Watch the home dashboard's <strong>Tx total</strong> increment
            within 5 seconds (next indexer poll), and the tx flips to
            <strong> FINAL</strong> after the next witness round.
          </p>
        </div>
      )}
    </div>
  );
}
